import { ChangeDetectionStrategy, Component, effect, EventEmitter, input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

export interface DropdownButtonItem<T = string | number> {
    value: T;
    label: string;
    description?: string;
    recommended?: boolean;
}

@Component({
    selector: 'dropdown-button',
    templateUrl: './dropdown-button.component.html',
    styleUrl: './dropdown-button.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DropdownButtonComponent<T> implements ControlValueAccessor, OnInit {
    protected control: FormControl = new FormControl();

    public items = input.required<DropdownButtonItem<T>[]>();

    public fontSize = input<number>();

    public lineHeight = input<number>();

    public labelWrapper = input<string>('');

    @Output('onChange')
    public readonly onChangeItem = new EventEmitter<T>();

    protected label = '';

    private destroy$ = new Subject<void>();

    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
    private onChange = (url: string): void => { };

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private onTouched = (): void => { };

    constructor(
        private controlDirective: NgControl
    ) {
        this.controlDirective.valueAccessor = this;

        effect(() => {
            this.updateLabel();
        });
    }

    private updateLabel(): void {
        this.label = this.wrapLabel(this.items().find(item => item.value === this.control.value)?.label || '', this.labelWrapper());
    }

    public ngOnInit(): void {
        this.control = this.controlDirective.control as FormControl;

        this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateLabel());

        this.updateLabel();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    public writeValue(val: string): void {
        if (!this.control.value) {
            this.control.setValue(val);
        }
    }

    protected setValue(value: T): void {
        if (value !== this.control.value) {
            this.control.patchValue(value);
            this.control.markAsDirty();
            this.onChangeItem.emit(value);
        }
    }

    private wrapLabel(label: string, labelWrapper: string): string {
        if (labelWrapper.length !== 2) {
            return label;
        }

        const startWrapper = labelWrapper.charAt(0);
        const endWrapper = labelWrapper.charAt(1);

        return `${startWrapper} ${label} ${endWrapper}`;
    }
}
